import { Action, Mutation, Module } from "vuex-class-modules";
import Axios, { AxiosResponse } from "axios";
import AttachedDeviceModel from "@/models/AttachedDeviceModel";
import AxiosSecureModule from "./axiosSecureModule";
import { Log } from "@/plugins/Logging";
import { app } from "@/store";

@Module({ generateMutationSetters: true })
export default class SensorModule extends AxiosSecureModule {
    private static readonly ACTION_TIMEOUT_MS = 30000;
    private lastReconnectNoticeAt = 0;

    // --- State ---
    devices: Array<AttachedDeviceModel> = [];

    // If a device is expected to appear "connecting" right after load,
    // keep its id here until SetData() hydrates devices from server.
    private pendingConnectingDeviceIds = new Set<string>();

    // Device status ACK timestamps to avoid reverting from stale DB reads
    private lastStatusAckAt = new Map<string, number>(); // deviceId -> epoch ms

    // Per-device sync watchdogs (spinners/timeouts)
    private syncTimers = new Map<string, number>();   // deviceId -> interval id
    private syncSeconds = new Map<string, number>();  // deviceId -> seconds

    // Called when sensor is reconnecting
    private maybeNotifyReconnectInfo() {
        const now = Date.now();
        const RECONNECT_NOTICE_MS = 15 * 60 * 1000;
        if (now - this.lastReconnectNoticeAt >= RECONNECT_NOTICE_MS) {
            // use whatever toast you already show; you've used app.success/app.error earlier.
            // If you have app.info or a snackbar helper, call that instead.
            app.success = "After a restart, sensors can take up to 10 minutes to reconnect.";
            this.lastReconnectNoticeAt = now;
        }
    }

    // --- Internal: replace device object (Vue 2 reactive for new keys) ---
    private updateDevice(deviceId: string, patch: Record<string, any>) {
        const idx = this.devices.findIndex(d => d.attachedDeviceId === deviceId);
        if (idx === -1) return;

        const current = this.devices[idx] as any;
        const next = { ...current, ...patch }; // NEW object => new reactive shape
        // Replace the array slot so Vue 2 reactivity picks up *new* keys
        this.devices.splice(idx, 1, next);
    }

    // --- Private helpers (UI-only flags stored on device objects) ---
    private markBusy(deviceId: string, busy: boolean) {
        this.updateDevice(deviceId, { isBusy: busy });
    }

    private startDeviceSync(deviceId: string) {
        this.stopDeviceSync(deviceId);  // reset if already running
        this.markBusy(deviceId, true);
        this.syncSeconds.set(deviceId, 0);

        const tick = () => {
            const s = (this.syncSeconds.get(deviceId) ?? 0) + 1;
            this.syncSeconds.set(deviceId, s);
            if (s * 1000 >= SensorModule.ACTION_TIMEOUT_MS) {
                app.error = "Sensor unresponsive.";

                // Revert toggle to last stable state (what we knew from ACK/DB)
                const d = this.devices.find(x => x.attachedDeviceId === deviceId) as any;
                if (d) {
                    const fallback = d.stableStatus ?? d.status;
                    this.updateDevice(deviceId, { status: fallback });
                }

                this.stopDeviceSync(deviceId);
                this.GetData(); // refresh list as a fallback
            }
        };

        const id = window.setInterval(tick, 1000);
        this.syncTimers.set(deviceId, id);
    }

    private stopDeviceSync(deviceId: string) {
        const id = this.syncTimers.get(deviceId);
        if (id) {
            clearInterval(id);
            this.syncTimers.delete(deviceId);
        }
        this.syncSeconds.delete(deviceId);
        this.markBusy(deviceId, false);
    }

    // --- API calls / Actions ---
    @Action
    public async GetData() {
        await this.defineHeaderAxios();
        Axios.get("api/sensor")
            .then((result: AxiosResponse) => this.SetData(result.data))
            .catch((error) => Log.error("Get sensor data failed", error));
    }

    /**
     * Toggle a device (switch/relay/etc.).
     * For door/hatch (or anything that shouldn't show a spinner), pass noSync: true.
     */
    @Action
    public async SetDeviceStatus(payload: { id: string; status: boolean; noSync?: boolean }) {
        if (!payload.noSync) {
            // Start per-device spinner while server processes the toggle
            this.startDeviceSync(payload.id);
        }

        await this.defineHeaderAxios();
        Axios.put(`api/sensor/status/${payload.id}/${payload.status}`)
            .then(() => {
                Log.information("SetDeviceStatus sent.");
                // We stop the spinner on ACKs via SetSensorStatus/SetSensorAge.
            })
            .catch((error) => {
                if (!payload.noSync) {
                    this.stopDeviceSync(payload.id);
                }
                Log.error("SetDeviceStatus failed", error);
            });
    }

    /**
     * Door/hatch control: same endpoint as switches but WITHOUT sync/busy.
     */
    @Action
    public async SetDoorState(payload: { id: string; state: boolean }) {
        return this.SetDeviceStatus({ id: payload.id, status: payload.state, noSync: true });
    }

    @Action
    public async SetHatchState(payload: { id: string; state: boolean }) {
        return this.SetDeviceStatus({ id: payload.id, status: payload.state, noSync: true });
    }

    @Action
    public async GetCoreHistory() {
        await this.defineHeaderAxios();
        Axios.get(`/api/sensor/CoreHistory`)
            .then(() => Log.information("Core history fetched"))
            .catch((error) => Log.error("GetCoreHistory failed", error));
    }

    @Action
    public async GetDeviceHistory(id: string) {
        await this.defineHeaderAxios();
        Axios.get(`/api/sensor/history/${id}`)
            .then(() => Log.information("Device history fetched"))
            .catch((error) => Log.error("GetDeviceHistory failed", error));
    }

    @Action
    public async RequestAdcCalibration(channel: number) {
        await this.defineHeaderAxios();
        Axios.get(`api/sensor/calibrate/${channel}`)
            .then(() => Log.information("ADC calibration requested"))
            .catch((error) => Log.error("RequestAdcCalibration failed", error));
    }

    // ACK from server/push: update status and stop spinner for this device
    @Action
    public async SetSensorStatus(payload: { device: string; status: boolean }) {
        this.updateDevice(payload.device, { status: payload.status, stableStatus: payload.status });
        this.lastStatusAckAt.set(payload.device, Date.now());
        this.stopDeviceSync(payload.device);
        Log.information(`SensorModule: status updated id=${payload.device}, status=${payload.status}`);
    }

    // Battery updates do not necessarily indicate action ACK; do not stop sync
    @Action
    public async SetSensorBattery(payload: { device: string; battery: number }) {
        this.updateDevice(payload.device, { battery: payload.battery });
        Log.information(`SensorModule: battery updated id=${payload.device}, battery=${payload.battery}`);
    }

    // Age updates: when age becomes valid (not -1), device answered -> stop spinner
    @Action
    public async SetSensorAge(payload: {
        device: string;
        status?: string;
        age: string;
        atPosition?: any;
    }) {
        if (this.devices.length === 0) {
            Log.information("SensorModule: No devices paired.");
            return;
        }
        const dev = this.devices.find(d => d.attachedDeviceId === payload.device) as any;
        if (!dev) {
            Log.information(`SensorModule: Device not found id=${payload.device}`);
            return;
        }

        const prevConnecting = !!dev.isConnecting;             // <-- track previous
        const ageNum = Number(payload.age);
        const isConnecting = ageNum === -1;

        this.updateDevice(payload.device, { age: ageNum, isConnecting });

        if (isConnecting && !prevConnecting) {                 // <-- notify on flip
            //this.maybeNotifyReconnectInfo();
        }

        if (!isConnecting) this.stopDeviceSync(payload.device);

        Log.information(
            `SensorModule: age updated id=${payload.device}, age=${ageNum}, connecting=${isConnecting}`
        );
    }

    @Action
    public async GetSensorAge(id: string) {
        const dev = this.devices.find(item => item.attachedDeviceId === id);
        return dev?.age;
    }

    @Action
    public async UnpairDevice(attachedDeviceId: string) {
        await this.defineHeaderAxios();
        // unpair can be slow; show spinner for the device
        this.startDeviceSync(attachedDeviceId);
        Axios.delete(`api/sensor/unpair/${encodeURIComponent(attachedDeviceId)}`)
            .then(() => {
                Log.information("Unpair sent.");
                this.GetData();
            })
            .catch((error) => {
                this.stopDeviceSync(attachedDeviceId);
                Log.error("Unpair failed.", error);
                app.error = "Unpair failed";
            });
    }

    // --- Mutations ---
    @Mutation
    SetData(data: Array<AttachedDeviceModel>) {
        const now = Date.now();
        const SAFETY_MS = 2000; // ignore older DB rows for 2s after an ACK

        const withUi = data.map(d => {
            const prev = this.devices.find(x => x.attachedDeviceId === d.attachedDeviceId) as any;

            // Map DB 'alarm1' to our UI 'status' (true = on/open)
            const dbStatus = typeof (d as any).alarm1 === "boolean" ? (d as any).alarm1 : undefined;

            // If we recently got an ACK for this device and the DB row looks older,
            // keep the in-memory status to avoid flicker/flipping back.
            const ackAt = this.lastStatusAckAt.get(d.attachedDeviceId || "") || 0;
            const serverMod = Date.parse((d as any).modifiedDateTime || "1970-01-01T00:00:00Z");
            const usePrevStatus = prev?.status !== undefined && (serverMod + SAFETY_MS) < ackAt;

            const status = usePrevStatus ? prev.status : dbStatus;
            const stableStatus = dbStatus !== undefined ? dbStatus : (prev?.stableStatus ?? prev?.status);

            return {
                ...(d as any),
                status,
                stableStatus,
                isBusy: prev?.isBusy ?? false,
                isConnecting: (d as any).age === -1
            };
        });

        this.devices.splice(0, this.devices.length, ...withUi);

        let anyNewConnecting = false;
        withUi.forEach(d => {
            const prev = this.devices.find(x => x.attachedDeviceId === d.attachedDeviceId) as any;
            // (If you prefer, track prev before splice; or just check d.isConnecting)
            if ((d as any).isConnecting === true) anyNewConnecting = true;
        });
        //if (anyNewConnecting) this.maybeNotifyReconnectInfo();

        // Re-apply any queued connecting flags
        this.pendingConnectingDeviceIds.forEach(id => {
            this.updateDevice(id, { isConnecting: true });
        });
        this.pendingConnectingDeviceIds.clear();
    }

    @Mutation
    SetConnectingState(deviceId: string) {
        const dev = this.devices.find(d => d.attachedDeviceId === deviceId) as any;

        if (!dev) {
            // device list not hydrated yet — queue and still show the toast
            this.pendingConnectingDeviceIds.add(deviceId);       // <-- queue
            //this.maybeNotifyReconnectInfo();                     // <-- notify
            return;
        }

        const wasConnecting = !!dev.isConnecting;              // <-- track previous
        this.updateDevice(deviceId, { isConnecting: true });

        if (!wasConnecting) {                                  // <-- notify on flip
            //this.maybeNotifyReconnectInfo();
        }
    }

    @Mutation
    ClearConnectingState(deviceId: string) {
        this.updateDevice(deviceId, { isConnecting: false });
    }
}